import {
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Title,
  Image,
} from '@mantine/core';
import { badRequest, normalizeOperationOutcome } from '@medplum/core';
import type { OperationOutcome } from '@medplum/fhirtypes';
import {
  Document,
  Form,
  MedplumLink,
  OperationOutcomeAlert,
  getErrorsForInput,
  getIssuesForExpression,
  useMedplum,
} from '@medplum/react';
import { FC, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import logo from '/ainigma-logo-small.png';

// -----------------------------------------------------------------------------
export const SetPasswordPage: FC = () => {
  const { id, secret } = useParams() as { id: string; secret: string };
  const medplum = useMedplum();
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation(['signin-page']);
  const issues = getIssuesForExpression(outcome, undefined);

  // ---------------------------------------------------------------------------
  return (
    <Document width={450}>
      <OperationOutcomeAlert issues={issues} />
      <Form
        onSubmit={(formData: Record<string, string>) => {
          if (formData.password !== formData.confirmPassword) {
            setOutcome(
              badRequest(
                t('setPassword.passwordMismatchError', { ns: 'signin-page' }),
                'confirmPassword'
              )
            );
            return;
          }
          setOutcome(undefined);
          const body = {
            id,
            secret,
            password: formData.password,
          };
          medplum
            .post('auth/setpassword', body)
            .then(() => setSuccess(true))
            .catch((err) => setOutcome(normalizeOperationOutcome(err)));
        }}
      >
        <Center style={{ flexDirection: 'column' }}>
          <Image src={logo} alt="Logo" w={32} h={32} />
          <Title>{t('setPassword.title', { ns: 'signin-page' })}</Title>
        </Center>
        {!success && (
          <Stack>
            <PasswordInput
              name="password"
              label={t('setPassword.newPasswordLabel', { ns: 'signin-page' })}
              required={true}
              error={getErrorsForInput(outcome, 'password')}
            />
            <PasswordInput
              name="confirmPassword"
              label={t('setPassword.confirmPasswordLabel', {
                ns: 'signin-page',
              })}
              required={true}
              error={getErrorsForInput(outcome, 'confirmPassword')}
            />
            <Group justify="flex-end" mt="xl">
              <Button type="submit">
                {t('setPassword.button', { ns: 'signin-page' })}
              </Button>
            </Group>
          </Stack>
        )}
        {success && (
          <div data-testid="success">
            {t('setPassword.successMessage', { ns: 'signin-page' })}&nbsp;
            <MedplumLink to="/signin">
              {t('setPassword.signInLink', { ns: 'signin-page' })}
            </MedplumLink>
            .
          </div>
        )}
      </Form>
    </Document>
  );
};
